import * as React from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { CLAIM, type XyqarMfa, type XyqarPrincipal, type XyqarRole, type XyqarTenant } from "./types";

export interface XyqarAuthConfig {
  domain:       string;   // e.g. xyqar.eu.auth0.com
  clientId:     string;   // per-app
  audience:     string;   // https://api.xyqar.com
  redirectUri?: string;
  scope?:       string;
}

interface Ctx {
  principal:    XyqarPrincipal | null;
  loading:      boolean;
  login:        (options?: any) => void;
  loginWithMfa: (options?: any) => void;
  logout:       () => void;
  getToken:     () => Promise<string>;
}

const AuthCtx = React.createContext<Ctx | null>(null);

function readPrincipal(user: any | undefined): XyqarPrincipal | null {
  if (!user?.sub) return null;
  const roles  = (user[CLAIM.ROLES]  as XyqarRole[] | undefined) ?? [];
  const tenant = (user[CLAIM.TENANT] as XyqarTenant   | undefined) ?? null;
  const mfa    = (user[CLAIM.MFA]    as XyqarMfa      | undefined) ?? { totp_verified: false, methods: [] };
  return {
    sub:    user.sub,
    email:  user.email ?? "",
    name:   user.name,
    roles,
    tenant,
    mfa,
  };
}

const Inner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const a = useAuth0();
  const principal = readPrincipal(a.user);
  const value: Ctx = {
    principal,
    loading: a.isLoading,
    login:   (options?: any) => a.loginWithRedirect(options),
    // Force Auth0 to require MFA (TOTP) at login.
    // acr_values "mfa" matches Auth0's MFA policy enrollment + challenge.
    loginWithMfa: (options?: any) => a.loginWithRedirect({
      ...options,
      authorizationParams: {
        ...(options?.authorizationParams ?? {}),
        acr_values: "http://schemas.openid.net/pape/policies/2007/06/multi-factor",
      },
    }),
    logout:  () => a.logout({ logoutParams: { returnTo: window.location.origin } }),
    getToken: () => a.getAccessTokenSilently(),
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const XyqarAuthProvider: React.FC<{ config: XyqarAuthConfig; children: React.ReactNode }> = ({
  config, children,
}) => (
  <Auth0Provider
    domain={config.domain}
    clientId={config.clientId}
    cacheLocation="localstorage"
    useRefreshTokens={!(import.meta as any).env?.VITE_E2E_MODE}
    useRefreshTokensFallback={!(import.meta as any).env?.VITE_E2E_MODE}
    authorizationParams={{
      audience:    config.audience,
      redirect_uri: config.redirectUri ?? window.location.origin,
      scope:       config.scope ?? "openid profile email offline_access",
    }}
  >
    <Inner>{children}</Inner>
  </Auth0Provider>
);

export function useXyqarAuth() {
  const ctx = React.useContext(AuthCtx);
  if (!ctx) throw new Error("useXyqarAuth: missing <XyqarAuthProvider>");
  return ctx;
}

export function useTenant(): XyqarTenant | null {
  return useXyqarAuth().principal?.tenant ?? null;
}

export function hasRole(p: XyqarPrincipal | null, ...any: XyqarRole[]) {
  if (!p) return false;
  return any.some(r => p.roles.includes(r));
}

export function requireMfa(p: XyqarPrincipal | null) {
  return !!p?.mfa.totp_verified;
}
